<template>
	<SimpleModal @close="close">
		<div
			class="omnibox-container"
			@keydown.prevent.down="handleDown"
			@keydown.prevent.up="handleUp"
			@keydown.prevent.tab.exact="handleDown"
			@keydown.prevent.shift.tab="handleUp"
			@keydown.prevent.enter="launchSelected()"
			@click="$refs.input && $refs.input.focus()"
		>
			<div class="omnibox-search-wrapper">
				<Input
					v-model="search"
					:icon="SearchIcon"
					:placeholder="$t('contacts_userpicker_search_placeholder')"
					autoFocus
					ref="input"
				/>
				<div class="search-options" ref="listcontainer">
					<div v-for="(item, index) in filtered" :key="item.id">
						<TextDivider
							v-if="showCategory(index, item.category)"
							:text="item.category"
							:textStyle="{
								color: '#9498a3',
								fontSize: '14px',
								fontFamily: 'Avenir-Medium',
							}"
							style="margin: 16px 0;"
						/>
						<a
							class="search-item"
							:class="{ selected: index === selected }"
							@mousemove="handleHover(index)"
							@mouseleave="hovering = false"
							@click.prevent.stop="launchSelected()"
						>
							<component v-if="item.icon" :is="item.icon" class="action-icon" />
							<span v-text="item.name" />
						</a>
					</div>
				</div>
			</div>
		</div>
	</SimpleModal>
</template>

<script>
import store, {
	getCategories,
	getChatsForCategory,
	getChatsWithNoCategory,
	getDMChats,
	canSendAnnouncements,
} from '@/store'
import { sortBubblesByTitle } from '@/helpers/utils'
import { SimpleModal, TextDivider, Input } from '@/components/common'
import {
	CommentsIcon,
	UserGroupIcon,
	BullhornIcon,
	CalendarCheckIcon,
	LinkIcon,
	SearchIcon,
} from '@/icons'
import scrollIntoView from 'scroll-into-view-if-needed'
import { redirectToRecentsOrChat } from '@/helperMethods'
import { markRaw } from 'vue'

export default {
	components: {
		Input,
		SimpleModal,
		TextDivider,
		CommentsIcon,
		UserGroupIcon,
		BullhornIcon,
		CalendarCheckIcon,
		LinkIcon,
	},
	data() {
		return {
			store,
			search: '',
			selected: 0,
			SearchIcon: markRaw(SearchIcon),
		}
	},

	emits: ['close'],

	watch: {
		search: function (val) {
			this.selected = 0
			this.scrollIntoViewIfNeeded()
		},
	},

	computed: {
		actions() {
			const actions = []
			if (store.checkOrgPermission('create_dm')) {
				actions.push({
					icon: CommentsIcon,
					name: this.$t('chat_drawer_addbubble_directmessage'),
					action: 'create_direct_message',
				})
			}
			if (store.checkOrgPermission('create_group')) {
				actions.push({
					icon: UserGroupIcon,
					name: this.$t('chat_drawer_addbubble_create'),
					action: 'create_group',
				})
			}
			actions.push({
				icon: LinkIcon,
				name: this.$t('groupinvites_join_title'),
				action: 'enter_group_code',
			})
			if (canSendAnnouncements()) {
				actions.push({
					icon: BullhornIcon,
					name: this.$t('chat_drawer_addbubble_announcement'),
					action: 'send_announcement',
				})
			}
			if (store.tasksEnabled()) {
				actions.push({
					icon: CalendarCheckIcon,
					name: this.$t('chat_drawer_addbubble_task'),
					action: 'create_task',
				})
			}
			return actions.map((a) => ({
				...a,
				category: this.$t('chat_drawer_addbubble_title'),
			}))
		},

		categories() {
			return getCategories()
				.map((cat) => {
					return getChatsForCategory(cat.id)
						.map((bubble) => this.assignCategoryAndName(bubble, cat.title))
						.sort(sortBubblesByTitle)
				})
				.flat()
		},

		noncategories() {
			return getChatsWithNoCategory()
				.map((bubble) =>
					this.assignCategoryAndName(
						bubble,
						this.$t('chat_drawer_section_bubbles_uncatagorized')
					)
				)
				.slice()
				.sort(sortBubblesByTitle)
		},

		dms() {
			return getDMChats()
				.map((bubble) =>
					this.assignCategoryAndName(
						bubble,
						this.$t('chat_drawer_section_directmessages')
					)
				)
				.slice()
				.sort(sortBubblesByTitle)
		},

		filtered() {
			// TODO: we would get better results if we used Fuse here
			const all = [
				...this.actions,
				...this.categories,
				...this.noncategories,
				...this.dms,
			]

			if (this.search) {
				const re = new RegExp(this.search.replace(/\s/g, ''), 'gi')
				return all
					.filter((item) => {
						return item.name.replace(/\s/g, '').match(re)
					})
					.concat({
						icon: SearchIcon,
						name: this.$t('search_display_term', { Term: `"${this.search}"` }),
						action: 'search',
					})
			}
			return all
		},
	},

	methods: {
		assignCategoryAndName(chat, category) {
			return Object.assign({}, chat, {
				category,
				name: chat.displayname,
				action: 'jump_to_group',
			})
		},

		close() {
			this.$emit('close')
		},

		handleDown() {
			this.hovering = false
			this.selected === this.filtered.length - 1
				? this.filtered.length - 1
				: (this.selected += 1)
			this.scrollIntoViewIfNeeded()
		},

		handleHover(index) {
			this.hovering = true
			this.selected = index
		},

		handleUp() {
			this.hovering = false
			this.selected === 0 ? 0 : (this.selected -= 1)
			this.scrollIntoViewIfNeeded()
		},

		scrollIntoViewIfNeeded() {
			const target = this.$refs.listcontainer.querySelector('.selected')
			if (!target) return

			scrollIntoView(target, {
				scrollMode: 'if-needed',
				block: 'center',
				inline: 'nearest',
				behavior: 'smooth',
			})
		},

		launchSelected() {
			const action = this.filtered[this.selected].action
			switch (action) {
				case 'search':
					this.$router.push({
						name: 'search',
						query: { q: this.search },
					})
					break
				case 'jump_to_group':
					const groupId = this.filtered[this.selected].id
					redirectToRecentsOrChat(groupId)
					break
				default:
					EventBus.$emit('omniboxAction', action)
					break
			}
			this.close()
		},

		showCategory(idx, category) {
			const first_index_of_category = this.filtered.findIndex(
				(item) => item.category === category
			)
			return first_index_of_category === idx
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.omnibox-container {
	width: 590px;
	background: var(--neutral-0);
	color: var(--neutral-800);
	padding: 60px 40px 40px 40px;
	@include flex-column;

	.pronto-input {
		margin-bottom: 16px;
	}

	.search-options {
		height: 300px;
		overflow-y: auto;
		outline: 0;
		flex-grow: 1;

		.search-item {
			cursor: pointer;
			display: block;
			height: 35px;
			padding: 8px 5px 0 10px;
			border-radius: 6px;
			outline: 0;
			text-decoration: none;
			&.selected {
				background-color: var(--teal-100);
				> span,
				.action-icon {
					color: var(--teal-300);
				}
			}
			> span {
				display: inline-block;
				color: var(--neutral-800);
				@include ellipsis;
				max-width: 480px;
			}
			.action-icon {
				height: 18px;
				margin-right: 10px;
				vertical-align: top;
			}
		}
	}

	:deep(.text-divider-container) {
		.text {
			@include ellipsis;
			max-width: 300px;
			flex-grow: 1;
			flex-shrink: 0;
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.omnibox-container {
		background: var(--neutral-0);
		.text-divider-container {
			.line {
				border-color: var(--neutral-40040);
			}
		}

		.search-options {
			height: 300px;
			overflow-y: auto;
			outline: 0;
			flex-grow: 1;
			.search-item {
				&.selected {
					background-color: var(--neutral-300);
					> span,
					.action-icon {
						color: var(--neutral-800);
					}
				}
			}
		}
	}
}

@include overrideContrast {
	.omnibox-container {
		.search-options {
			.search-item {
				&.selected {
					> span,
					.action-icon {
						color: var(--neutral-100);
					}
				}
			}
		}
	}
}
</style>
